import React from "react";
import { AssociatedPerson, StructureControlProps } from "../../../../types/cdd-records.types";
import {
  beneficialOwnerHeading,
  isBeneficialOwnerRole,
} from "../../../../services/cddRecordsApiConfig";
import { roleLabel } from "../shared/associatedPersonsConfig";

function initials(p: AssociatedPerson) {
  return `${p.firstName?.[0] ?? ''}${p.lastName?.[0] ?? ''}`.toUpperCase();
}

function PersonCard({
  person,
  roleLabel,
  evidenceSources,
  avatarGradient,
  cardBg,
  showOwnership,
  onClick,
}: {
  person: AssociatedPerson;
  roleLabel: string;
  evidenceSources?: string[];
  avatarGradient: string;
  cardBg: string;
  showOwnership?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200/60 cursor-pointer hover:shadow-md transition-shadow ${cardBg}`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0 shadow-md ${avatarGradient}`}>
        {initials(person)}
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-900">
          {person.firstName} {person.lastName}
        </p>
        <p className="text-xs text-slate-400 mt-0.5">{roleLabel}</p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {evidenceSources?.map((source) => (
          <span
            key={source}
            className="rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200"
          >
            {source}
          </span>
        ))}
        {showOwnership && person.ownershipPercentage !== undefined && (
          <span className="text-sm font-bold text-gray-700">{person.ownershipPercentage}%</span>
        )}
      </div>
    </div>
  );
}

function PersonnelColumn({
  title,
  subtitle,
  count,
  emptyText,
  icon,
  headerText,
  countBadge,
  avatarGradient,
  cardBg,
  children,
}: {
  title: string;
  subtitle: string;
  count: number;
  emptyText: string;
  icon: React.ReactNode;
  headerText: string;
  countBadge: string;
  avatarGradient: string;
  cardBg: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200/60 overflow-hidden shadow-sm">
      <div className="px-5 py-4 bg-white border-b border-slate-200/60">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <span className={`font-extrabold text-xs uppercase tracking-wide ${headerText}`}>{title}</span>
          </div>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${countBadge}`}>{count}</span>
        </div>
        <p className={`text-[11px] mt-1 text-slate-400 italic`}>{subtitle}</p>
      </div>
      <div className="p-3 space-y-2 bg-white">
        {count === 0
          ? <p className="text-sm text-slate-400 text-center py-6">{emptyText}</p>
          : React.Children.map(children, (child) =>
              React.isValidElement(child)
                ? React.cloneElement(child as React.ReactElement<any>, { avatarGradient, cardBg })
                : child
            )}
      </div>
    </div>
  );
}

export const StructureControl: React.FC<StructureControlProps> = ({
  structure,
  associatedPersons,
  onPersonClick,
  entityType,
}) => {
  const directors = associatedPersons.filter((p) =>
    p.roles.includes("director"),
  );
  // AL-519 item 7: matches both ids. A literal "beneficial-owner" check here
  // would have emptied this column for every association.
  const beneficialOwners = associatedPersons.filter((p) =>
    p.roles.some(isBeneficialOwnerRole),
  );
  const secretaries = associatedPersons.filter((p) =>
    p.roles.includes("secretary"),
  );

  return (
    <div className="space-y-6">
      {/* Shareholding Structure */}
      {structure?.shareholderStructure && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Shareholding Structure
            </h3>
          </div>
          <div className="p-6">
            {/* Share Classes */}
            {structure.shareholderStructure.shareClasses && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Share Classes
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {structure.shareholderStructure.shareClasses.map(
                    (sc, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-900">
                          {sc.className}
                        </div>
                        <div className="text-2xl font-bold text-blue-600">
                          {sc.totalShares.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {sc.votingRights
                            ? "Voting Rights"
                            : "No Voting Rights"}
                        </div>
                      </div>
                    ),
                  )}
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-900">
                      Total
                    </div>
                    <div className="text-2xl font-bold text-blue-600">
                      {structure.shareholderStructure.totalShares.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">Total Shares</div>
                  </div>
                </div>
              </div>
            )}

            {/* Shareholders Table */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Shareholders
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Share Class
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Shares Held
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Ownership %
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Beneficial Owner
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {structure.shareholderStructure.shareholders.map(
                      (shareholder, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {shareholder.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 capitalize">
                            {shareholder.type}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {shareholder.shareClass}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right font-mono">
                            {shareholder.sharesHeld.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-500 h-2 rounded-full"
                                  style={{
                                    width: `${Math.min(shareholder.percentageOwnership, 100)}%`,
                                  }}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-900">
                                {shareholder.percentageOwnership.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {shareholder.beneficialOwner ? (
                              <span className="inline-flex items-center justify-center w-5 h-5 bg-green-100 rounded-full">
                                <svg
                                  className="w-3 h-3 text-green-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={3}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Corporate Structure */}
      {(structure?.parentCompany ||
        (structure?.subsidiaries && structure.subsidiaries.length > 0)) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Corporate Structure
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Parent Company */}
              {structure?.parentCompany && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Parent Company
                  </h4>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="font-medium text-gray-900">
                      {structure.parentCompany.name}
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-gray-500">
                      {structure.parentCompany.abn && (
                        <div>ABN: {structure.parentCompany.abn}</div>
                      )}
                      {structure.parentCompany.acn && (
                        <div>ACN: {structure.parentCompany.acn}</div>
                      )}
                      {structure.parentCompany.country && (
                        <div>Country: {structure.parentCompany.country}</div>
                      )}
                      {structure.parentCompany.ownershipPercentage && (
                        <div className="flex items-center gap-2 mt-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{
                                width: `${structure.parentCompany.ownershipPercentage}%`,
                              }}
                            />
                          </div>
                          <span className="font-medium text-gray-900">
                            {structure.parentCompany.ownershipPercentage}%
                            ownership
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Subsidiaries */}
              {structure?.subsidiaries && structure.subsidiaries.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Subsidiaries ({structure.subsidiaries.length})
                  </h4>
                  <div className="space-y-3">
                    {structure.subsidiaries.map((sub, index) => (
                      <div
                        key={index}
                        className="p-4 border border-gray-200 rounded-lg"
                      >
                        <div className="font-medium text-gray-900">
                          {sub.name}
                        </div>
                        <div className="mt-2 space-y-1 text-sm text-gray-500">
                          {sub.abn && <div>ABN: {sub.abn}</div>}
                          {sub.acn && <div>ACN: {sub.acn}</div>}
                          {sub.country && <div>Country: {sub.country}</div>}
                          {sub.ownershipPercentage && (
                            <div className="flex items-center gap-2 mt-2">
                              <div className="w-24 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-green-500 h-2 rounded-full"
                                  style={{
                                    width: `${sub.ownershipPercentage}%`,
                                  }}
                                />
                              </div>
                              <span className="font-medium text-gray-900">
                                {sub.ownershipPercentage}% ownership
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Key Personnel */}
      <div className={`grid grid-cols-1 ${secretaries.length > 0 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
            {/* Directors. Subtitle says "entity" not "company": this panel is
                rendered for every entity type, so an association was being told
                about a company. */}
            <PersonnelColumn
              title="Directors"
              subtitle="Directors recorded for the entity"
              count={directors.length}
              emptyText="No directors on record"
              headerText="text-indigo-700"
              countBadge="bg-indigo-100 text-indigo-700"
              avatarGradient="bg-gradient-to-br from-indigo-500 to-blue-600"
              cardBg="bg-indigo-50/40"
              icon={
                <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              }
            >
              {directors.map((director) => (
                <PersonCard
                  key={director.id}
                  person={director}
                  roleLabel="Director"
                  evidenceSources={director.roleEvidence?.director}
                  avatarGradient="bg-gradient-to-br from-indigo-500 to-blue-600"
                  cardBg="bg-indigo-50/40"
                  onClick={() => onPersonClick?.(director)}
                />
              ))}
            </PersonnelColumn>

            {/* Beneficial Owners */}
            {/* AL-519 item 7: the hedge is gone — the heading now names the
                population correctly for this entity type, because the per-person
                tag does too. */}
            <PersonnelColumn
              title={beneficialOwnerHeading(entityType)}
              subtitle="Individuals who ultimately own or control the entity"
              count={beneficialOwners.length}
              emptyText="No beneficial owners identified"
              headerText="text-purple-700"
              countBadge="bg-purple-100 text-purple-700"
              avatarGradient="bg-gradient-to-br from-purple-500 to-violet-600"
              cardBg="bg-purple-50/40"
              icon={
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            >
              {beneficialOwners.map((owner) => (
                <PersonCard
                  key={owner.id}
                  person={owner}
                  /*
                    AL-519 item 7: was hardcoded "Beneficial Owner", so an
                    association rendered the heading "Senior Managing Officials"
                    with every card beneath it still reading "Beneficial Owner".
                    A FIFTH label renderer, found in a browser after the unit
                    tests passed — the ticket's table lists three.

                    Reads the person's own mapped role, so the card cannot
                    disagree with the heading above it again.
                  */
                  roleLabel={roleLabel(
                    owner.roles.find(isBeneficialOwnerRole) ?? 'beneficial-owner',
                  )}
                  showOwnership={true}
                  avatarGradient="bg-gradient-to-br from-purple-500 to-violet-600"
                  cardBg="bg-purple-50/40"
                  onClick={() => onPersonClick?.(owner)}
                />
              ))}
            </PersonnelColumn>

            {/* Secretaries — hidden when empty */}
            {secretaries.length > 0 && (
            <PersonnelColumn
              title="Company Secretaries"
              subtitle="Responsible for company administration and compliance"
              count={secretaries.length}
              emptyText="No secretary on record"
              headerText="text-amber-700"
              countBadge="bg-amber-100 text-amber-700"
              avatarGradient="bg-gradient-to-br from-amber-500 to-orange-500"
              cardBg="bg-amber-50/40"
              icon={
                <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
            >
              {secretaries.map((secretary) => (
                <PersonCard
                  key={secretary.id}
                  person={secretary}
                  roleLabel="Secretary"
                  avatarGradient="bg-gradient-to-br from-amber-500 to-orange-500"
                  cardBg="bg-amber-50/40"
                  onClick={() => onPersonClick?.(secretary)}
                />
              ))}
            </PersonnelColumn>
            )}
          </div>
    </div>
  );
};

export default StructureControl;
